import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Star } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const discount = product.compare_at_price
    ? Math.round(100 - (product.price / product.compare_at_price) * 100)
    : 0;

  const handleAdd = (e) => {
    e.preventDefault();
    addItem(product, 1);
    toast.success(`${product.title} added to cart!`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.06 }}
    >
      <Link
        to={`/product/${product.id}`}
        className="group block bg-white rounded-3xl border-2 border-ink shadow-hard overflow-hidden transition-all hover:shadow-hard-lg hover:-translate-y-1"
        data-testid={`product-card-${product.id}`}
      >
        <div className="relative aspect-square overflow-hidden border-b-2 border-ink bg-secondary">
          <img src={product.image} alt={product.title} loading="lazy"
               className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          {discount > 0 && (
            <span className="absolute top-3 left-3 bg-brand-pink text-white rounded-full px-3 py-1 text-xs font-black uppercase border-2 border-ink shadow-hard-sm">
              -{discount}%
            </span>
          )}
        </div>
        <div className="p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-brand mb-1">{product.category}</p>
          <h3 className="font-bold font-heading leading-tight mb-2 line-clamp-2 min-h-[2.5rem]">{product.title}</h3>
          <div className="flex items-center gap-1 mb-2 h-4" data-testid={`product-rating-${product.id}`}>
            {product.review_count > 0 ? (
              <>
                <Star className="w-3.5 h-3.5 fill-brand-yellow text-ink" />
                <span className="text-xs font-bold">{product.avg_rating}</span>
                <span className="text-xs text-muted-foreground">({product.review_count})</span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">New</span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-black" data-testid={`product-price-${product.id}`}>${product.price.toFixed(2)}</span>
              {product.compare_at_price && (
                <span className="text-sm text-muted-foreground line-through">${product.compare_at_price.toFixed(2)}</span>
              )}
            </div>
            <button onClick={handleAdd}
                    className="w-10 h-10 rounded-full bg-brand-yellow border-2 border-ink flex items-center justify-center shadow-hard-sm hover:bg-brand hover:text-white transition-colors active:translate-y-0.5"
                    data-testid={`add-to-cart-${product.id}`} title="Add to cart">
              <Plus className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
